import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'

import type { EmojiClickData } from 'emoji-picker-react'
import type { Descendant } from 'slate'

import { EmojiPickerPopover } from '@/components/ui/emoji-picker-popover'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context'
import type { ParagraphElement } from '@/modules/message/components/placeholder/custom-types'
import {
  PlaceholderEditor,
  nodes,
} from '@/modules/message/components/placeholder/placeholder-editor'
import { classNames } from '@/utils/helpers'

function isEmptyParagraph(node: Descendant) {
  const children = (node as ParagraphElement)?.children

  return (
    typeof children === 'object' &&
    Array.isArray(children) &&
    children.length === 0
  )
}

const empty = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
] satisfies Descendant[]

export const MessageTextSelector = (props: ComponentProps<'div'>) => {
  const { className, ...rest } = props
  const { editor, defaultValues, placeholder } = useMessageEditor()

  const initialValue = useMemo(() => {
    if (defaultValues?.text) {
      const initial = nodes.parse(defaultValues?.text)
      // Slate does not accept empty paragraphs in the beginning of a text
      const [firstElement] = initial

      return firstElement && isEmptyParagraph(firstElement) ? [] : initial
    }

    return empty
  }, [defaultValues?.text])

  const handleEmojiClick = useCallback(
    (data: EmojiClickData) => {
      editor.insertText(data.emoji)
    },
    [editor],
  )

  return (
    <div
      {...rest}
      className={classNames('flex flex-col gap-1.5', className)}
    >
      <label className="text-sm font-medium text-text-default">Text</label>
      <div className="relative flex flex-grow items-center">
        <div className="w-full overflow-y-auto rounded-md border bg-card px-3 py-2 text-title focus-within:border-primary-500">
          <PlaceholderEditor
            placeholder={placeholder}
            editor={editor}
            initialValue={initialValue}
            className="h-40 focus:outline-0"
          />
        </div>
        <div className="absolute bottom-2 right-4">
          <EmojiPickerPopover
            onEmojiClick={handleEmojiClick}
            small
          />
        </div>
      </div>
    </div>
  )
}
